'use client'

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { XIcon } from 'lucide-react'
import type * as React from 'react'
import { createContext, useContext, useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

/**
 * `keepTyping`: once something's been typed in it, Escape and a click outside
 * no longer close the dialog — Cancel does, and the footer says so
 * (docs/UX-GUIDE.md: close on Escape and the backdrop unless it holds unsaved
 * input). The shared FormDialog does the same for every Majik form.
 */
const TypedInto = createContext<{ typed: () => void; held: boolean } | null>(null)

function Dialog({
  keepTyping = false,
  open,
  onOpenChange,
  ...props
}: DialogPrimitive.Root.Props & { keepTyping?: boolean }) {
  const [dirty, setDirty] = useState(false)
  const [held, setHeld] = useState(false)
  useEffect(() => {
    if (open) {
      setDirty(false)
      setHeld(false)
    }
  }, [open])
  if (!keepTyping) {
    return (
      <DialogPrimitive.Root data-slot="dialog" open={open} onOpenChange={onOpenChange} {...props} />
    )
  }
  return (
    <TypedInto.Provider value={{ typed: () => setDirty(true), held }}>
      <DialogPrimitive.Root
        data-slot="dialog"
        open={open}
        onOpenChange={(next, details) => {
          const casual = details?.reason === 'escape-key' || details?.reason === 'outside-press'
          if (!next && dirty && casual) return setHeld(true)
          onOpenChange?.(next, details)
        }}
        {...props}
      />
    </TypedInto.Provider>
  )
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      // Always drawn, a popup opened from another (an edit from Messages' details)
      // included — Base UI leaves a nested one's out — and dark enough to say
      // "you're in a popup" (Gideon, Oct 26).
      forceRender
      className={cn(
        'fixed inset-0 isolate z-50 bg-black/25 duration-200 dark:bg-black/50 ease-out supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
}) {
  const typedInto = useContext(TypedInto)
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        onInputCapture={() => typedInto?.typed()}
        data-slot="dialog-content"
        className={cn(
          'fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-6 rounded-xl bg-popover p-6 text-base text-popover-foreground shadow-floating ring-1 ring-foreground/10 duration-200 ease-out outline-none md:max-w-lg data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <Button
                variant="ghost"
                className="absolute top-4 right-4 max-md:top-2 max-md:right-2"
                size="icon-sm"
              />
            }
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="dialog-header" className={cn('flex flex-col gap-2', className)} {...props} />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  const held = useContext(TypedInto)?.held
  return (
    <>
      {held ? (
        <p role="status" className="text-sm text-muted-foreground">
          You have unsaved changes — save them, or Cancel to leave without saving.
        </p>
      ) : null}
      <div
        data-slot="dialog-footer"
        className={cn(
          // One footer for every popup: Cancel on the left, the action on the right, at every width.
          '-mx-6 -mb-6 flex flex-row justify-end gap-3 rounded-b-xl border-t bg-muted/50 px-6 py-4 max-md:*:flex-1',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close render={<Button variant="outline" />}>Close</DialogPrimitive.Close>
        )}
      </div>
    </>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-lg font-semibold', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
